import {
  Button,
  Checkbox,
  ListBox,
  ListBoxItem,
  Popover,
  Select,
  SelectValue,
  type Key,
} from "react-aria-components";
import Check from "src/design-system/icons/Check.svg?react";
import type { FeatureName, FeatureValue } from "../../feature/FeatureConfig";

type Props = {
  featureName: FeatureName;
  valueIndex: number;
  possibleFeatureValues: readonly FeatureValue<FeatureName>[];
  setFeature: (featureName: FeatureName, valueIndex: number) => void;
};

export function DebugFeatureInput({
  featureName,
  valueIndex,
  possibleFeatureValues,
  setFeature,
}: Props) {
  const onCheckboxChange = (isSelected: boolean) => {
    setFeature(featureName, isSelected ? 1 : 0);
  };

  const onSelectionChange = (key: Key | null) => {
    if (key) {
      const index = possibleFeatureValues.indexOf(
        key as FeatureValue<FeatureName>,
      );
      setFeature(featureName, index);
    }
  };

  return (
    <>
      {isBooleanFeature(possibleFeatureValues) ? (
        <Checkbox
          className="flex h-4.5 w-4.5 cursor-pointer items-center justify-center border-1 border-input-border"
          data-testid={`${featureName}-Test`}
          value={String(possibleFeatureValues[valueIndex])}
          isSelected={possibleFeatureValues[valueIndex] === true}
          defaultSelected={possibleFeatureValues[valueIndex]}
          onChange={onCheckboxChange}
        >
          {({ isSelected }) => isSelected && <Check className="h-3 w-3" />}
        </Checkbox>
      ) : (
        <Select
          data-testid={`${featureName}-Test`}
          defaultSelectedKey={possibleFeatureValues[valueIndex] as Key}
          onSelectionChange={onSelectionChange}
          aria-label="Select Feature Value"
        >
          <Button className="min-w-37.5 cursor-pointer border-1 border-border p-1 text-left">
            <SelectValue />
          </Button>
          <Popover
            placement="bottom left"
            offset={0}
            className="min-w-37.5 border-1 border-border bg-surface"
          >
            <ListBox>
              {possibleFeatureValues.map((featureState, index) => {
                return (
                  <ListBoxItem
                    className="cursor-pointer p-1 hover:bg-surface-hover data-selected:bg-surface-active"
                    id={featureState as string}
                    key={index}
                  >
                    {featureState}
                  </ListBoxItem>
                );
              })}
            </ListBox>
          </Popover>
        </Select>
      )}
    </>
  );
}

function isBooleanFeature(
  possibleValues: readonly FeatureValue<FeatureName>[],
): possibleValues is [boolean, boolean] {
  return (
    possibleValues.includes(true) &&
    possibleValues.includes(false) &&
    possibleValues.length === 2
  );
}
